import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DiscoveryMapEntry {
  ativo_id: string;
  last_seen_at: string;
  agent_version: string | null;
}

/** Returns a map of ativo_id -> DiscoveryMapEntry for all discovered assets. */
export const useDiscoveryMap = () => {
  return useQuery({
    queryKey: ['discovery_device_map'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('discovery_device_map')
        .select('ativo_id, last_seen_at, agent_version');

      if (error) throw error;

      const map = new Map<string, DiscoveryMapEntry>();
      for (const row of data ?? []) {
        // Keep the most recent entry if multiple device_ids point to same ativo
        const existing = map.get(row.ativo_id);
        if (!existing || row.last_seen_at > existing.last_seen_at) {
          map.set(row.ativo_id, row as DiscoveryMapEntry);
        }
      }
      return map;
    },
    staleTime: 60_000,
  });
};

/**
 * Returns color class and label based on last_seen_at age.
 * green  → synced < 26 h ago
 * yellow → 26 h – 7 days ago
 * red    → > 7 days ago
 */
export function discoveryBadgeColor(lastSeenAt: string): {
  color: string;
  label: string;
} {
  const diffMs = Date.now() - new Date(lastSeenAt).getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 26) {
    return { color: 'text-green-500', label: `Sync há ${Math.round(diffHours)}h` };
  }
  const diffDays = diffHours / 24;
  if (diffDays < 7) {
    return { color: 'text-yellow-500', label: `Sync há ${Math.round(diffDays)} dias` };
  }
  return { color: 'text-red-500', label: `Sem sync há ${Math.round(diffDays)} dias` };
}
